import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import HeroSectionV2 from "../../components/HeroSectionV2";
import Billede1 from "../../Images/Billede1.jpg";
import TeamPersonModalForm from "./TeamPersonModalForm";
import TeamPersonsList from "./TeamPersonList";
import { Person, EventDto, PersonFunction, FunctionOption } from "../Admin/ID/types";
import { getAuthHeaders } from "./../../api/authHelper";
interface FunctionsByEvent {
    eventID: number;
    functions: string[];
}

type PersonWithFunctionsByEvent = Person & {
    functionsByEvent?: FunctionsByEvent[];
    uploadImageBase64?: string;
};

const TeamPerson: React.FC = () => {
    const { eventId, userId } = useParams();

    const apiUrl = process.env.REACT_APP_API_URL!;
    const numericEventId = Number(eventId);
    const numericUserId = Number(userId);

    const [persons, setPersons] = useState<Person[]>([]);
    const [event, setEvent] = useState<EventDto | null>(null);
    const [functions, setFunctions] = useState<FunctionOption[]>([]);

    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [addingPerson, setAddingPerson] = useState(false);
    const [activeTab, setActiveTab] = useState<"all" | "event">(numericEventId ? "event" : "all");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [selectedPersons, setSelectedPersons] = useState<Person[]>([]);
    const [moveFunctionId, setMoveFunctionId] = useState<number>(0);

    const normalizeCategory = (c?: string) =>
        (c ?? "").replace(/\r?\n/g, "").trim().toLowerCase();

    const normalizePerson = (
        p: any,
        eventFunctions: FunctionOption[]
    ): Person => ({
        ...p,
        userId: p.userId ?? p.user?.id ?? numericUserId,
        events: p.events ?? [],
        functions: Array.isArray(p.functions)
            ? p.functions
                .map((f: any) => {
                    // already correct format
                    if (
                        typeof f === "object" &&
                        f.functionId &&
                        f.functionName
                    ) {
                        return {
                            functionId: f.functionId,
                            functionName: f.functionName,
                            eventId: f.eventId ?? numericEventId,
                        } as PersonFunction;
                    }

                    // string fallback
                    if (typeof f === "string") {
                        return {
                            functionId: 0,
                            functionName: f,
                            eventId: numericEventId,
                        } as PersonFunction;
                    }

                    // backend weird shape fallback
                    if (f?.name) {
                        return {
                            functionId: f.functionId ?? 0,
                            functionName: f.name,
                            eventId: f.eventId ?? numericEventId,
                        } as PersonFunction;
                    }

                    return null;
                })
                .filter(Boolean)
            : [],
        zones: p.zones ?? [],
        personImagePath: p.personImagePath ?? "",
        profileUrl: p.personImagePath ?? "",
        hasProfileImage: p.hasImage ?? !!p.personImagePath,
    });

    useEffect(() => {
        if (!numericEventId || !numericUserId) return;

        const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
        const eventRes = await fetch(
            `${apiUrl}/api/events/${numericEventId}/light`,
            { headers: getAuthHeaders() }
        );

        if (!eventRes.ok) {
            throw new Error(`Event error: ${eventRes.status}`);
        }

        const eventData = await eventRes.json();
        setEvent(eventData);

        const funcRes = await fetch(
            `${apiUrl}/api/events/${numericEventId}/functions`,
            { headers: getAuthHeaders() }
        );

        if (!funcRes.ok) {
            throw new Error(`Functions error: ${funcRes.status}`);
        }

        const functionsData = await funcRes.json();

        const eventFunctions: FunctionOption[] = functionsData.map((f: any) => ({
            functionId: f.functionId,
            functionName: f.functionName?.trim() ?? "Unknown Function",
            eventID: numericEventId,
            categoryName: normalizeCategory(f.categoryName),
        }));

        setFunctions(eventFunctions);

        const personsRes = await fetch(
            `${apiUrl}/api/persons/by-user?userId=${numericUserId}`,
            { headers: getAuthHeaders() }
        );

        if (!personsRes.ok) {
            throw new Error(`Persons error: ${personsRes.status}`);
        }

        const personsData = await personsRes.json();

        setPersons(
            personsData.map((p: any) =>
                normalizePerson(p, eventFunctions)
            )
        );

    } catch (err: any) {
        console.error("FETCH ERROR DETAILS:", err);
        setError(err.message || "Error fetching data");
    } finally {
        setLoading(false);
    }
};

        fetchData();
    }, [apiUrl, numericEventId, numericUserId]);

    useEffect(() => {
        setActiveTab(numericEventId ? "event" : "all");
    }, [numericEventId]);

    const handleCreate = async (newPerson: PersonWithFunctionsByEvent) => {
        try {
            const dto = {
                ...newPerson,
                userId: numericUserId,
                events: event ? [event] : [],
                functions:
                    newPerson.functions?.map((f) => ({
                        functionId: f.functionId,
                        functionName: f.functionName,
                        eventId: numericEventId,
                    })) ?? [],
                uploadImageBase64: newPerson.uploadImageBase64 ?? "",
                personImagePath: newPerson.personImagePath ?? "",
            };

            const res = await fetch(`${apiUrl}/api/persons`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Create failed:", errorText);
                alert("Error Creating Person");
                return;
            }

            const created = normalizePerson(await res.json(), functions);
            setPersons((prev) => [...prev, created]);
            setAddingPerson(false);
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdate = async (person: PersonWithFunctionsByEvent) => {
        try {
            const dto = {
                ...person,
                events: person.events ?? [],
                functions:
                    person.functions?.map((f) => ({
                        functionId: f.functionId,
                        functionName: f.functionName,
                        eventId: f.eventId,
                    })) ?? [],
                uploadImageBase64: person.uploadImageBase64 ?? "",
                personImagePath: person.personImagePath ?? "",
            };

            const res = await fetch(`${apiUrl}/api/persons/${person.personId}`, {
                method: "PUT",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(dto),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Update failed:", errorText);
                alert("Error Updating Person");
                return;
            }

            const updated = normalizePerson(await res.json(), functions);
            setPersons((prev) => prev.map((p) => (p.personId === updated.personId ? updated : p)));
            setSelectedPerson(null);
        } catch (err) {
            console.error(err);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Delete Person?")) return;
        try {
            const res = await fetch(`${apiUrl}/api/persons/${id}`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });
            if (!res.ok) {
                alert("Error Deleting Person");
                return;
            }
            setPersons((prev) => prev.filter((p) => p.personId !== id));
        } catch (err) {
            console.error(err);
        }
    };

    const handleMoveSelectedToEvent = async () => {
        if (selectedPersons.length === 0) {
            alert("Vælg mindst én person.");
            return;
        }

        if (!moveFunctionId) {
            alert("Vælg en funktion.");
            return;
        }

        const res = await fetch(`${apiUrl}/api/persons/move-to-event`, {
            method: "POST",
            headers: {
                ...getAuthHeaders(),
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                newEventId: numericEventId,
                personIds: selectedPersons.map(p => p.personId),
                functionIds: [moveFunctionId],
            }),
        });

        if (!res.ok) {
            const text = await res.text();
            alert("Kunne ikke tilføje personer: " + text);
            return;
        }

        alert(`${selectedPersons.length} personer blev tilføjet til eventet.`);

        window.location.reload();
    };

    const userPersons = persons.filter((p) => p.userId === numericUserId);
    const userEventPersons = userPersons.filter((p) => p.events?.some((e) => e.eventID === numericEventId));

    if (!numericEventId || !numericUserId) return <div style={{ padding: 20 }}>No EventID or UserID</div>;
    if (loading) return <div style={{ padding: 20 }}>Loading...</div>;
    if (error) return <div style={{ padding: 20, color: "red" }}>{error}</div>;

    

    return (
        <>
            <HeroSectionV2
                titleEn="Team Accreditations"
                titleDa="Team Akkrediteringer"
                descriptionEn={event ? `Event: ${event.eventName}` : "Loading..."}
                descriptionDa={event ? `Event: ${event.eventName}` : "Indlæser..."}
                imageSrc={Billede1}
                badgeText="TEAM"
                logoSrc={event?.eventLogoPath ?? ""}
            />

            <div className="team-person-page">
                <div className="team-person-header-card">
                    <div>
                        <h2 className="team-person-title">
                            {event?.eventName}
                        </h2>
                    </div>

                    <button
                        className="team-person-add-btn"
                        onClick={() => setAddingPerson(true)}
                    >
                        + Add a Person to Team
                    </button>
                </div>

                <div className="team-person-tabs">
                    <button
                        onClick={() => {
                            setSelectedPersons([]);
                            setMoveFunctionId(0);
                            setActiveTab("all");
                        }}
                        className={`team-person-tab ${activeTab === "all" ? "active" : ""}`}
                    >
                        My Persons
                    </button>

                    <button
                        onClick={() => {
                            setSelectedPersons([]);
                            setMoveFunctionId(0);
                            setActiveTab("event");
                        }}
                        className={`team-person-tab ${activeTab === "event" ? "active" : ""}`}
                    >
                        My Persons for this event
                    </button>
                </div>


                {addingPerson && (
                    <TeamPersonModalForm
                        initialPerson={null}
                        onSave={handleCreate}
                        onCancel={() => setAddingPerson(false)}
                        allEvents={event ? [event] : []}
                        allFunctions={functions
                            .filter(f => normalizeCategory(f.categoryName) === "team")
                            .map(f => ({
                                id: f.functionId,
                                name: f.functionName
                            }))
                        }
                        currentUserId={numericUserId}
                        currentEventID={numericEventId}
                    />
                )}

                {selectedPerson && (
                    <TeamPersonModalForm
                        initialPerson={selectedPerson}
                        onSave={handleUpdate}
                        onCancel={() => setSelectedPerson(null)}
                        allEvents={event ? [event] : []}
                        allFunctions={functions
                            .filter(f => normalizeCategory(f.categoryName) === "team")
                            .map(f => ({
                                id: f.functionId,
                                name: f.functionName
                            }))
                        }
                        currentUserId={numericUserId}
                        currentEventID={numericEventId}
                    />
                )}
                {activeTab === "all" && selectedPersons.length > 0 && (
                    <div className="move-selected-card">
                        <div>
                            <strong>{selectedPersons.length}</strong> personer valgt
                            <div className="move-selected-sub">
                                Tilføj markerede personer til {event?.eventName}
                            </div>
                        </div>

                        <select
                            value={moveFunctionId}
                            onChange={(e) => setMoveFunctionId(Number(e.target.value))}
                        >
                            <option value={0}>Vælg funktion</option>
                            {functions
                                .filter(f => normalizeCategory(f.categoryName) === "team")
                                .map(f => (
                                    <option key={f.functionId} value={f.functionId}>
                                        {f.functionName}
                                    </option>
                                ))}
                        </select>

                        <button
                            className="team-person-add-btn"
                            onClick={handleMoveSelectedToEvent}
                        >
                            Tilføj til event
                        </button>
                    </div>
                )}
                {activeTab === "all" ? (
                    <TeamPersonsList
                        persons={userPersons}
                        onEdit={(id) => setSelectedPerson(persons.find((p) => p.personId === id) || null)}
                        onDelete={handleDelete}
                        tableId="team-persons-all"
                        currentEventId={numericEventId}
                        onSelectPersons={setSelectedPersons}
                    />
                ) : (
                    <TeamPersonsList
                        persons={userEventPersons}
                        onEdit={(id) => setSelectedPerson(persons.find((p) => p.personId === id) || null)}
                        onDelete={handleDelete}
                        tableId="team-persons-event"
                        currentEventId={numericEventId}
                    />
                )}
            </div>
        </>
    );
};

export default TeamPerson;